import { supabase } from '@/integrations/supabase/client';

export interface ContentItem {
  id: number;
  category_id: number;
  content_key: string;
  content_value: string;
  content_type: 'text' | 'html' | 'json';
  is_active: boolean;
  ghana_context: Record<string, unknown>;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  category?: {
    name: string;
    description: string;
  };
}

export interface ContentCategory {
  id: number;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

export interface SMSTemplate {
  id: number;
  template_name: string;
  template_key: string;
  message_template: string;
  variables: string[];
  ghana_context: Record<string, unknown>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface EmailTemplate {
  id: number;
  template_name: string;
  template_key: string;
  subject_template: string;
  html_template: string;
  text_template?: string;
  variables: string[];
  ghana_context: Record<string, unknown>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BusinessSchedule {
  id: number;
  day_of_week: number; // 0=Sunday, 6=Saturday
  is_open: boolean;
  open_time: string | null;
  close_time: string | null;
  ghana_timezone: string;
  special_notes: string | null;
  is_active: boolean;
}

export interface SpecialClosure {
  id: number;
  closure_name: string;
  closure_type: 'holiday' | 'maintenance' | 'emergency';
  start_date: string;
  end_date: string;
  closure_message: string;
  show_alternative_message: boolean;
  alternative_message: string | null;
  is_active: boolean;
  ghana_context: Record<string, unknown>;
  created_at: string;
}

export interface StoreStatus {
  id: number;
  status: 'open' | 'closed' | 'maintenance' | 'limited';
  status_message: string | null;
  override_schedule: boolean;
  temporary_until: string | null;
  ghana_context: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export class ContentManagementService {
  // Content Management Methods
  async getContent(contentKey: string): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('content_management')
        .select('content_value')
        .eq('content_key', contentKey)
        .eq('is_active', true)
        .single();

      if (error || !data) {
        console.warn(`Content not found for key: ${contentKey}`);
        return null;
      }

      return data.content_value;
    } catch (error) {
      console.error('Error fetching content:', error);
      return null;
    }
  }

  async getContentWithFallback(contentKey: string, fallback: string): Promise<string> {
    const content = await this.getContent(contentKey);
    return content || fallback;
  }

  async getAllContent(): Promise<ContentItem[]> {
    try {
      const { data, error } = await supabase
        .from('content_management')
        .select(`
          *,
          category:content_categories(name, description)
        `)
        .order('content_key');

      if (error) {
        console.error('Error fetching all content:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAllContent:', error);
      return [];
    }
  }

  async getContentByCategory(categoryName: string): Promise<ContentItem[]> {
    try {
      const { data, error } = await supabase
        .from('content_management')
        .select(`
          *,
          category:content_categories!inner(name, description)
        `)
        .eq('content_categories.name', categoryName)
        .eq('is_active', true)
        .order('content_key');

      if (error) {
        console.error('Error fetching content by category:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getContentByCategory:', error);
      return [];
    }
  }

  async updateContent(id: number, updates: Partial<ContentItem>): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('content_management')
        .update({
          ...updates,
          updated_by: (await supabase.auth.getUser()).data.user?.id
        })
        .eq('id', id);

      if (error) {
        console.error('Error updating content:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateContent:', error);
      return false;
    }
  }

  async createContent(content: Omit<ContentItem, 'id' | 'created_at' | 'updated_at'>): Promise<number | null> {
    try {
      const { data, error } = await supabase
        .from('content_management')
        .insert({
          ...content,
          created_by: (await supabase.auth.getUser()).data.user?.id,
          updated_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error creating content:', error);
        return null;
      }

      return data.id;
    } catch (error) {
      console.error('Error in createContent:', error);
      return null;
    }
  }

  // Content Categories
  async getContentCategories(): Promise<ContentCategory[]> {
    try {
      const { data, error } = await supabase
        .from('content_categories')
        .select('*')
        .order('name');

      if (error) {
        console.error('Error fetching content categories:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getContentCategories:', error);
      return [];
    }
  }

  // SMS Templates
  async getSMSTemplates(): Promise<SMSTemplate[]> {
    try {
      const { data, error } = await supabase
        .from('sms_templates')
        .select('*')
        .order('template_name');

      if (error) {
        console.error('Error fetching SMS templates:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getSMSTemplates:', error);
      return [];
    }
  }

  async getSMSTemplate(templateKey: string): Promise<SMSTemplate | null> {
    try {
      const { data, error } = await supabase
        .from('sms_templates')
        .select('*')
        .eq('template_key', templateKey)
        .eq('is_active', true)
        .single();

      if (error || !data) {
        console.warn(`SMS template not found for key: ${templateKey}`);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error fetching SMS template:', error);
      return null;
    }
  }

  async updateSMSTemplate(id: number, updates: Partial<SMSTemplate>): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('sms_templates')
        .update({
          ...updates,
          updated_by: (await supabase.auth.getUser()).data.user?.id
        })
        .eq('id', id);

      if (error) {
        console.error('Error updating SMS template:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateSMSTemplate:', error);
      return false;
    }
  }

  async createSMSTemplate(template: Omit<SMSTemplate, 'id' | 'created_at' | 'updated_at'>): Promise<number | null> {
    try {
      const { data, error } = await supabase
        .from('sms_templates')
        .insert({
          ...template,
          created_by: (await supabase.auth.getUser()).data.user?.id,
          updated_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error creating SMS template:', error);
        return null;
      }

      return data.id;
    } catch (error) {
      console.error('Error in createSMSTemplate:', error);
      return null;
    }
  }

  // Email Templates  
  async getEmailTemplates(): Promise<EmailTemplate[]> {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .order('template_name');

      if (error) {
        console.error('Error fetching email templates:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getEmailTemplates:', error);
      return [];
    }
  }

  async getEmailTemplate(templateKey: string): Promise<EmailTemplate | null> {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('template_key', templateKey)
        .eq('is_active', true)
        .single();

      if (error || !data) {
        console.warn(`Email template not found for key: ${templateKey}`);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error fetching email template:', error);
      return null;
    }
  }

  async updateEmailTemplate(id: number, updates: Partial<EmailTemplate>): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('email_templates')
        .update({
          ...updates,
          updated_by: (await supabase.auth.getUser()).data.user?.id
        })
        .eq('id', id);

      if (error) {
        console.error('Error updating email template:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateEmailTemplate:', error);
      return false;
    }
  }

  async createEmailTemplate(template: Omit<EmailTemplate, 'id' | 'created_at' | 'updated_at'>): Promise<number | null> {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .insert({
          ...template,
          created_by: (await supabase.auth.getUser()).data.user?.id,
          updated_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error creating email template:', error);
        return null;
      }

      return data.id;
    } catch (error) {
      console.error('Error in createEmailTemplate:', error);
      return null;
    }
  }

  // Business Schedule
  async getBusinessSchedule(): Promise<BusinessSchedule[]> {
    try {
      const { data, error } = await supabase
        .from('business_schedule')
        .select('*')
        .eq('is_active', true)
        .order('day_of_week');

      if (error) {
        console.error('Error fetching business schedule:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getBusinessSchedule:', error);
      return [];
    }
  }

  async updateBusinessSchedule(id: number, updates: Partial<BusinessSchedule>): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('business_schedule')
        .update(updates)
        .eq('id', id);

      if (error) {
        console.error('Error updating business schedule:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateBusinessSchedule:', error);
      return false;
    }
  }

  // Store Status and Closures
  async getCurrentStoreStatus(): Promise<StoreStatus | null> {
    try {
      const { data, error } = await supabase
        .from('store_status')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error || !data) {
        console.warn('No store status found');
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error fetching store status:', error);
      return null;
    }
  }

  async updateStoreStatus(statusUpdate: { is_open: boolean; message?: string; temporary_until?: string }): Promise<boolean> {
    try {
      // First, deactivate all previous status records
      await supabase
        .from('store_status')
        .update({ is_active: false })
        .eq('is_active', true);

      // Insert new status record
      const { error } = await supabase
        .from('store_status')
        .insert({
          is_open: statusUpdate.is_open,
          status: statusUpdate.is_open ? 'open' : 'closed',
          message: statusUpdate.message,
          status_message: statusUpdate.message,
          override_schedule: statusUpdate.override_schedule || false,
          is_active: true,
          created_by: (await supabase.auth.getUser()).data.user?.id
        });

      if (error) {
        console.error('Error updating store status:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateStoreStatus:', error);
      return false;
    }
  }

  async getSpecialClosures(): Promise<SpecialClosure[]> {
    try {
      const { data, error } = await supabase
        .from('special_closures')
        .select('*')
        .eq('is_active', true)
        .order('start_date');

      if (error) {
        console.error('Error fetching special closures:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getSpecialClosures:', error);
      return [];
    }
  }

  async createSpecialClosure(closureData: { reason: string; start_date: string; end_date?: string; message_override?: string }): Promise<number | null> {
    try {
      const { data, error } = await supabase
        .from('special_closures')
        .insert({
          reason: closureData.reason,
          closure_name: closureData.reason,
          closure_type: 'maintenance',
          start_date: closureData.start_date,
          end_date: closureData.end_date || null,
          message_override: closureData.message_override || null,
          closure_message: closureData.message_override || `Store closed: ${closureData.reason}`,
          is_active: true,
          created_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error creating special closure:', error);
        return null;
      }

      return data.id;
    } catch (error) {
      console.error('Error in createSpecialClosure:', error);
      return null;
    }
  }

  // Store Hours Logic
  async isStoreOpen(): Promise<{ isOpen: boolean; message: string; reason?: string }> {
    try {
      // Check store status override first
      const storeStatus = await this.getCurrentStoreStatus();
      if (storeStatus?.override_schedule) {
        return {
          isOpen: storeStatus.status === 'open',
          message: storeStatus.status_message || `Store is ${storeStatus.status}`,
          reason: 'status_override'
        };
      }

      // Check for special closures
      const today = new Date().toISOString().split('T')[0];
      const closures = await this.getSpecialClosures();
      const activeClosures = closures.filter(closure => 
        closure.start_date <= today && closure.end_date >= today
      );

      if (activeClosures.length > 0) {
        const closure = activeClosures[0];
        return {
          isOpen: false,
          message: closure.closure_message,
          reason: `special_closure_${closure.closure_type}`
        };
      }

      // Check regular business schedule
      const now = new Date();
      const accraTime = new Date(now.toLocaleString("en-US", {timeZone: "Africa/Accra"}));
      const dayOfWeek = accraTime.getDay();
      const currentTime = accraTime.toTimeString().slice(0, 5); // HH:MM format

      const schedule = await this.getBusinessSchedule();
      const todaySchedule = schedule.find(s => s.day_of_week === dayOfWeek);

      if (!todaySchedule || !todaySchedule.is_open) {
        return {
          isOpen: false,
          message: 'We are closed today',
          reason: 'not_business_day'
        };
      }

      if (!todaySchedule.open_time || !todaySchedule.close_time) {
        return {
          isOpen: false,
          message: 'No operating hours set for today',
          reason: 'no_hours_set'
        };
      }

      const isWithinHours = currentTime >= todaySchedule.open_time && currentTime <= todaySchedule.close_time;

      return {
        isOpen: isWithinHours,
        message: isWithinHours 
          ? 'We are open and accepting orders!' 
          : `We are closed. Opening hours: ${todaySchedule.open_time} - ${todaySchedule.close_time}`,
        reason: isWithinHours ? 'open' : 'outside_business_hours'
      };

    } catch (error) {
      console.error('Error checking store hours:', error);
      return {
        isOpen: true, // Default to open on error
        message: 'Store status unavailable, but we are accepting orders',
        reason: 'error_default_open'
      };
    }
  }

  // Template Processing Utilities
  processTemplate(template: string, variables: Record<string, unknown>): string {
    let processed = template;
    
    for (const [key, value] of Object.entries(variables)) {
      const regex = new RegExp(`\\$\\{${key}\\}`, 'g');
      processed = processed.replace(regex, String(value || ''));
    }
    
    return processed;
  }

  async processSMSTemplate(templateKey: string, variables: Record<string, unknown>): Promise<string | null> {
    const template = await this.getSMSTemplate(templateKey);
    if (!template) {
      return null;
    }
    
    return this.processTemplate(template.message_template, variables);
  }

  async processEmailTemplate(templateKey: string, variables: Record<string, unknown>): Promise<{
    subject: string;
    html: string;
    text: string | null;
  } | null> {
    const template = await this.getEmailTemplate(templateKey);
    if (!template) {
      return null;
    }
    
    return {
      subject: this.processTemplate(template.subject_template, variables),
      html: this.processTemplate(template.html_template, variables),
      text: template.text_template ? this.processTemplate(template.text_template, variables) : null
    };
  }
}

// Export singleton instance
export const contentManagementService = new ContentManagementService();